import { useCallback, useEffect, useState } from "react";

import { send } from "../api/auth";
import {
  compliance as complianceApi,
  type WorkingTimeExceptionDraft,
  type WorkingTimeExceptions as ExceptionsView,
} from "../api/compliance";
import { console_ } from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { useSession } from "../session/SessionProvider";
import { typedTimeHint } from "../system/clock";
import { whenIn } from "../system/format";

// ---------------------------------------------------------------------------
// S0-176 (0312). A DOCUMENTED EXCEPTION TO A LEGAL WORKING-TIME LIMIT.
//
// Counsel: an unlawful schedule is refused unless an exception is documented with its
// legal ground, scope, supporting agreement and approving employer representative, and
// "event operations" is never one. Whoever records it here is that representative, and
// needs config.manage. The database decides every rule; this panel shows its refusal.
// Which limits may be lifted comes from the server, so no class is named in this file.
// ---------------------------------------------------------------------------

type Failure = { error?: { message?: string } };
type WorkerOption = { id: string; given_name: string; family_name: string };

export function WorkingTimeExceptions() {
  const { apiBase, me } = useSession();
  const { t, refusal } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const mayApprove = me?.authenticated === true && me.permissions.includes("config.manage");
  const [view, setView] = useState<ExceptionsView | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [workers, setWorkers] = useState<WorkerOption[]>([]);
  const [draft, setDraft] = useState<WorkingTimeExceptionDraft>({
    worker_id: "", kind: "", valid_from: "", valid_until: "", legal_ground: "", supporting_agreement: "",
  });
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<{ tone: "live" | "failed"; message: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await send<ExceptionsView & Failure>(complianceApi.workingTimeExceptions(apiBase));
      if (res.status === 200 && Array.isArray(res.body.exceptions)) {
        setView(res.body);
        setFailed(null);
      } else {
        setFailed(refusal(res.body.error) ?? t("The working-time exceptions could not be read."));
      }
    } catch {
      setFailed(t("We could not reach Verbunk."));
    }
  }, [apiBase]);

  useEffect(() => {
    void load();
    if (!mayApprove) return;
    void (async () => {
      const res = await send<{ workers?: WorkerOption[] }>(console_.listWorkers(apiBase, { limit: 200 }));
      setWorkers(res.status === 200 ? (res.body.workers ?? []) : []);
    })();
  }, [load, apiBase, mayApprove]);

  const set = (k: keyof WorkingTimeExceptionDraft) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setDraft({ ...draft, [k]: e.target.value });

  async function record(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setOutcome(null);
    try {
      const res = await send<Failure>(complianceApi.recordWorkingTimeException(apiBase, {
        ...draft,
        valid_from: draft.valid_from ? new Date(draft.valid_from).toISOString() : "",
        valid_until: draft.valid_until ? new Date(draft.valid_until).toISOString() : "",
      }));
      if (res.status === 200) {
        setOutcome({ tone: "live", message: t("The exception is recorded.") });
        setDraft({ worker_id: "", kind: "", valid_from: "", valid_until: "", legal_ground: "", supporting_agreement: "" });
        await load();
      } else {
        setOutcome({ tone: "failed", message: refusal(res.body.error) ?? t("That exception was not recorded.") });
      }
    } catch {
      setOutcome({ tone: "failed", message: t("We could not reach Verbunk.") });
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    if (busy) return;
    setBusy(true);
    try {
      const res = await send<Failure>(complianceApi.revokeWorkingTimeException(apiBase, id));
      if (res.status !== 200) setOutcome({ tone: "failed", message: refusal(res.body.error) ?? t("That exception was not revoked.") });
      await load();
    } catch {
      setOutcome({ tone: "failed", message: t("We could not reach Verbunk.") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>{t("Working-time exceptions")}</h2>
      <p className="muted">
        {t("A legal working-time limit is lifted only for one worker and one window, with its legal ground, the agreement behind it and the person approving it. Event operations is never a legal ground.")}
      </p>
      {failed !== null ? (
        <p className="muted">{failed}</p>
      ) : view === null ? (
        <p className="muted">{t("Loading…")}</p>
      ) : view.exceptions.length === 0 ? (
        <p className="muted">{t("No exception is recorded, so every legal limit refuses.")}</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>{t("Worker")}</th>
              <th>{t("Limit")}</th>
              <th>{t("From")}</th>
              <th>{t("Until")}</th>
              <th>{t("Legal ground")}</th>
              <th>{t("Agreement")}</th>
              <th>{t("Approved by")}</th>
              <th><span className="visually-hidden">{t("State")}</span></th>
            </tr>
          </thead>
          <tbody>
            {view.exceptions.map((x) => (
              <tr key={x.id}>
                <td>{x.worker_name}</td>
                <td>{x.kind_label}</td>
                <td>{whenIn(x.valid_from, orgZone)}</td>
                <td>{whenIn(x.valid_until, orgZone)}</td>
                <td>{x.legal_ground}</td>
                <td>{x.supporting_agreement}</td>
                <td>{x.approved_by ?? "—"}</td>
                <td>
                  {x.revoked_at ? (
                    <StatusChip tone="neutral" label={t("Revoked")} />
                  ) : mayApprove ? (
                    <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => void revoke(x.id)}>
                      {t("Revoke")}
                    </button>
                  ) : (
                    <StatusChip tone="live" label={t("In force")} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {mayApprove && view !== null ? (
        <form className="stack" onSubmit={(e) => void record(e)}>
          <div className="field">
            <label htmlFor="wte-worker">{t("Worker")}</label>
            <select id="wte-worker" required value={draft.worker_id} onChange={set("worker_id")}>
              <option value="">{t("Choose a worker")}</option>
              {workers.map((w) => (
                <option key={w.id} value={w.id}>{w.given_name} {w.family_name}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="wte-kind">{t("Limit")}</label>
            <select id="wte-kind" required value={draft.kind} onChange={set("kind")}>
              <option value="">{t("Choose a limit")}</option>
              {view.kinds.map((k) => (
                <option key={k.id} value={k.id}>{k.label}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="wte-from">{t("From")}</label>
            <input id="wte-from" type="datetime-local" required value={draft.valid_from} onChange={set("valid_from")} />
          </div>
          <div className="field">
            <label htmlFor="wte-until">{t("Until")}</label>
            <input id="wte-until" type="datetime-local" required value={draft.valid_until} onChange={set("valid_until")} />
          </div>
          <p className="field-hint">{typedTimeHint(orgZone, t)}</p>
          <div className="field">
            <label htmlFor="wte-ground">{t("Legal ground")}</label>
            <textarea id="wte-ground" required minLength={10} maxLength={2000} value={draft.legal_ground} onChange={set("legal_ground")} />
          </div>
          <div className="field">
            <label htmlFor="wte-agreement">{t("Supporting agreement")}</label>
            <input id="wte-agreement" required maxLength={500} value={draft.supporting_agreement} onChange={set("supporting_agreement")} />
          </div>
          <button type="submit" className="btn" disabled={busy}>
            {busy ? t("Working…") : t("Record the exception")}
          </button>
        </form>
      ) : null}
      {outcome ? (
        <div className="form-message" role="status" aria-live="polite">
          <StatusChip tone={outcome.tone} label={outcome.message} />
        </div>
      ) : null}
    </section>
  );
}
